/**
 * Analytics Service
 * 
 * Client-side analytics with:
 * - Queue management with localStorage backup
 * - Batching (5s interval or 10 events)
 * - Offline fallback with retry
 * - Backpressure handling
 * - Event deduplication
 * 
 * All events go through Supabase Edge Function (analytics-track)
 * NO direct Amplitude SDK or keys on client
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { handleEdgeFunctionError, extractErrorDetails } from './edgeFunctionErrorService';
import { getEdgeFunctionUrl } from '@/lib/config/environment';

// ============= CONFIGURATION =============

const ANALYTICS_ENABLED = import.meta.env.VITE_ANALYTICS_ENABLED !== 'false'; // default true
const BATCH_INTERVAL_MS = 5000; // 5 seconds
const BATCH_SIZE_THRESHOLD = 10; // send when 10 events accumulated
const MAX_QUEUE_SIZE = 1000; // max events in queue
const STORAGE_KEY = 'drug_analytics_queue';
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000]; // exponential backoff

// ============= TYPES =============

export interface AnalyticsEvent {
  event_id: string;
  event_type: string;
  screen_id?: string;
  timestamp: string;
  user_id?: string;
  anonymous_id?: string;
  session_id?: string;
  device_id?: string;
  schema_version: 'v1';
  properties?: Record<string, unknown>;
}

interface QueuedEvent extends AnalyticsEvent {
  _retryCount?: number;
}

interface BatchMeta {
  app_version?: string;
  env: 'local' | 'dev' | 'stage' | 'prod';
  source: 'web';
}

// ============= STATE =============

let eventQueue: QueuedEvent[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
let isSending = false;
let backoffMultiplier = 1;
let sessionId: string | null = null;
let anonymousId: string | null = null;

// ============= HELPERS =============

/**
 * Generate unique event ID for deduplication
 */
function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get or create session ID
 */
function getSessionId(): string {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('drug_session_id');
    if (!sessionId) {
      sessionId = `ses_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('drug_session_id', sessionId);
    }
  }
  return sessionId;
}

/**
 * Get or create anonymous ID
 */
function getAnonymousId(): string {
  if (!anonymousId) {
    anonymousId = localStorage.getItem('drug_anonymous_id');
    if (!anonymousId) {
      anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('drug_anonymous_id', anonymousId);
    }
  }
  return anonymousId;
}

/**
 * Get current environment
 */
function getEnvironment(): 'local' | 'dev' | 'stage' | 'prod' {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
  if (hostname.includes('dev') || hostname.includes('preview')) return 'dev';
  if (hostname.includes('stage') || hostname.includes('staging')) return 'stage';
  return 'prod';
}

/**
 * Get device info
 */
function getDeviceInfo(): Record<string, string> {
  const ua = navigator.userAgent;
  return {
    browser: getBrowserName(ua),
    os: getOSName(ua),
    device: getDeviceType(),
    lang: navigator.language,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function getBrowserName(ua: string): string {
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
}

function getOSName(ua: string): string {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

function getDeviceType(): string {
  if (/Mobi|Android/i.test(navigator.userAgent)) return 'mobile';
  if (/Tablet|iPad/i.test(navigator.userAgent)) return 'tablet';
  return 'desktop';
}

/**
 * Get UTM parameters from URL
 */
function getUTMParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  return utm;
}

// ============= QUEUE MANAGEMENT =============

/**
 * Load queue from localStorage
 */
function loadQueueFromStorage(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as QueuedEvent[];
      eventQueue = [...parsed, ...eventQueue];
      localStorage.removeItem(STORAGE_KEY);
      logger.debug('[analyticsService] Loaded queue from storage', { 
        context: { feature: 'analytics' },
        data: { count: parsed.length },
        allowList: ['count'],
      });
    }
  } catch (e) {
    logger.warn('[analyticsService] Failed to load queue from storage', { 
      context: { feature: 'analytics' },
      error: e 
    });
  }
}

/**
 * Save queue to localStorage (for page unload)
 */
function saveQueueToStorage(): void {
  if (eventQueue.length === 0) return;
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    const existingQueue = existing ? JSON.parse(existing) : [];
    const merged = [...existingQueue, ...eventQueue].slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    eventQueue = [];
  } catch (e) {
    logger.warn('[analyticsService] Failed to save queue to storage', { 
      context: { feature: 'analytics' },
      error: e 
    });
  }
}

/**
 * Add event to queue
 */
function enqueue(event: QueuedEvent): void {
  // Enforce queue size limit (drop oldest)
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    const dropped = eventQueue.shift();
    logger.warn('[analyticsService] Queue overflow, dropped oldest event', { 
      context: { feature: 'analytics' },
      data: { dropped_event_type: dropped?.event_type },
      allowList: ['dropped_event_type'],
    });
    trackHealthEvent('analytics_queue_overflow');
  }
  
  eventQueue.push(event);
  
  // Check if should send immediately
  if (eventQueue.length >= BATCH_SIZE_THRESHOLD) {
    scheduleBatch(0);
  } else if (!batchTimer) {
    scheduleBatch(BATCH_INTERVAL_MS * backoffMultiplier);
  }
}

/**
 * Schedule batch send
 */
function scheduleBatch(delayMs: number): void {
  if (batchTimer) {
    clearTimeout(batchTimer);
  }
  batchTimer = setTimeout(() => {
    batchTimer = null;
    sendBatch();
  }, delayMs);
}

// ============= BATCH SENDING =============

/**
 * Send batch of events to Edge Function
 */
async function sendBatch(): Promise<void> {
  if (isSending || eventQueue.length === 0 || !ANALYTICS_ENABLED) {
    return;
  }
  
  isSending = true;
  
  // Take events from queue
  const batch = eventQueue.splice(0, BATCH_SIZE_THRESHOLD);
  
  const meta: BatchMeta = {
    app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    env: getEnvironment(),
    source: 'web',
  };
  
  try {
    const { data, error } = await supabase.functions.invoke('analytics-track', {
      body: { events: batch, meta },
    });
    
    if (error) {
      throw error;
    }
    
    // Success - reset backoff
    backoffMultiplier = 1;
    
    logger.debug('[analyticsService] Batch sent', { 
      context: { feature: 'analytics' },
      data: { accepted: data?.accepted, rejected: data?.rejected },
      allowList: ['accepted', 'rejected'],
    });
    
    trackHealthEvent('analytics_batch_sent');
    
    // Continue sending if more events
    if (eventQueue.length > 0) {
      scheduleBatch(100);
    }
  } catch (error) {
    // Centralized error handling with Sentry/Amplitude
    handleEdgeFunctionError('analytics-track', error, {
      retryCount: batch[0]?._retryCount || 0,
      route: window.location.pathname,
      additionalData: { batchSize: batch.length },
    });
    
    // Determine retry strategy based on error type
    const details = extractErrorDetails(error);
    
    if (!details.isRetryable) {
      // Non-retryable errors — drop batch and log
      logger.error('[analyticsService] Non-retryable error, dropping batch', {
        context: { feature: 'analytics' },
        data: { 
          dropped_events: batch.length, 
          error_type: details.type,
        },
        allowList: ['dropped_events', 'error_type'],
        error_code: 'analytics_batch_dropped',
        error_type: 'network',
      });
      trackHealthEvent('analytics_batch_dropped', { 
        dropped_events: batch.length,
        error_type: details.type,
      });
    } else {
      // Retryable errors — put events back in queue
      const eventsToRetry = batch.map(e => ({
        ...e,
        _retryCount: (e._retryCount || 0) + 1,
      })).filter(e => e._retryCount <= RETRY_DELAYS.length);
      
      eventQueue = [...eventsToRetry, ...eventQueue];
      
      // Increase backoff
      backoffMultiplier = Math.min(backoffMultiplier * 2, 10);
      
      trackHealthEvent('analytics_batch_retry', {
        retry_count: eventsToRetry[0]?._retryCount || 0,
        error_type: details.type,
      });
      
      // Schedule retry
      const retryDelay = RETRY_DELAYS[Math.min(eventsToRetry[0]?._retryCount || 0, RETRY_DELAYS.length - 1)];
      scheduleBatch(retryDelay);
    }
  } finally {
    isSending = false;
  }
}

/**
 * Send remaining events immediately (for page unload)
 */
async function flushSync(): Promise<void> {
  if (eventQueue.length === 0) return;
  
  const batch = eventQueue.slice(0, BATCH_SIZE_THRESHOLD);
  const meta: BatchMeta = {
    app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    env: getEnvironment(),
    source: 'web',
  };
  
  const payload = JSON.stringify({ events: batch, meta });
  
  // Try sendBeacon first
  const url = getEdgeFunctionUrl('analytics-track');
  
  const sent = navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
  
  if (sent) {
    eventQueue = eventQueue.slice(BATCH_SIZE_THRESHOLD);
  } else {
    // Fallback to fetch with keepalive
    try {
      fetch(url, {
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      });
      eventQueue = eventQueue.slice(BATCH_SIZE_THRESHOLD);
    } catch {
      // Save to storage for next session
      saveQueueToStorage();
    }
  }
}

// ============= HEALTH EVENTS =============

/**
 * Track analytics health event (internal, no recursion)
 */
function trackHealthEvent(eventType: string, properties?: Record<string, unknown>): void {
  logger.event(eventType, {
    context: { feature: 'analytics_health', function_name: 'trackHealthEvent' },
    data: properties || {},
    allowList: Object.keys(properties || {}),
  });
}

// ============= PUBLIC API =============

export const analyticsService = {
  /**
   * Initialize analytics service
   */
  init(): void {
    if (!ANALYTICS_ENABLED) {
      logger.info('[analyticsService] Analytics disabled');
      return;
    }
    
    loadQueueFromStorage();
    
    // Handle page visibility
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushSync();
        saveQueueToStorage();
      }
    });
    
    // Handle page unload
    window.addEventListener('beforeunload', () => {
      flushSync();
      saveQueueToStorage();
    });
    
    window.addEventListener('pagehide', () => {
      flushSync();
      saveQueueToStorage();
    });
    
    // Handle online/offline
    window.addEventListener('online', () => {
      loadQueueFromStorage();
      if (eventQueue.length > 0) {
        scheduleBatch(100);
      }
    });
    
    logger.info('[analyticsService] Initialized');
  },
  
  /**
   * Track an event
   */
  track(eventType: string, properties?: Record<string, unknown>, screenId?: string): void {
    if (!ANALYTICS_ENABLED) return;
    
    const deviceInfo = getDeviceInfo();
    const utmParams = getUTMParams();
    
    const event: QueuedEvent = {
      event_id: generateEventId(),
      event_type: eventType,
      screen_id: screenId,
      timestamp: new Date().toISOString(),
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      schema_version: 'v1',
      properties: {
        ...properties,
        ...deviceInfo,
        ...utmParams,
        route: window.location.pathname,
        referrer: document.referrer || undefined,
      },
    };
    
    enqueue(event);
  },
  
  /**
   * Set user ID (after authentication)
   */
  setUserId(userId: string): void {
    // Store for future events
    sessionStorage.setItem('drug_user_id', userId);
  },
  
  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return sessionStorage.getItem('drug_user_id');
  },
  
  /**
   * Flush all pending events
   */
  async flush(): Promise<void> {
    await sendBatch();
  },
  
  // ============= CONVENIENCE METHODS =============
  
  /**
   * Create timestamp in ISO format
   */
  timestamp(): string {
    return new Date().toISOString();
  },
  
  // System events
  trackAppLaunch(properties?: Record<string, unknown>): void {
    this.track('app_launch_web', properties, 'SYS-001');
  },
  
  trackSessionRestoreSuccess(): void {
    this.track('session_restore_success', {}, 'SYS-001');
  },
  
  trackSessionRestoreFailed(reason: string): void {
    this.track('session_restore_failed', { reason }, 'SYS-001');
  },
  
  // Onboarding events
  trackOnboardingStart(fromPage: string): void {
    this.track('onboarding_start', { from_page: fromPage }, 'ONB-101');
  },
  
  trackOnboardingStepView(screenId: string, stepIndex: number): void {
    this.track('onboarding_step_view', { step_index: stepIndex }, screenId);
  },
  
  trackOnboardingCompleted(screenId: string, finalStep: number): void {
    this.track('onboarding_completed', { final_step: finalStep }, screenId);
  },
  
  trackOnboardingSkipped(screenId: string, lastViewedStep: number): void {
    this.track('onboarding_skipped', { last_viewed_step: lastViewedStep }, screenId);
  },
  
  // Catalog events
  trackCatalogView(screenId: string): void {
    this.track('catalog_view', {}, screenId);
  },
  
  /**
   * Track filter application with detailed payload for funnel analysis
   */
  trackCatalogFilterApply(
    screenId: string, 
    payload: {
      species: string | null;
      breed_ids: string[];
      breed_count: number;
      sex: string[];
      age_buckets: string[];
      geo_place_id: string | null;
      has_geo: boolean;
      sizes: string[];
      colors: string[];
      health_special_needs: boolean | null;
      filter_source: 'modal' | 'chip' | 'reset' | 'saved_search' | 'ai_profile';
      active_filters_count: number;
    }
  ): void {
    this.track('catalog_filter_apply', payload, screenId);
  },
  
  trackCatalogSearch(screenId: string, query: string): void {
    this.track('catalog_search', { query_length: query.length }, screenId);
  },
  
  // Favorites events
  trackFavoriteAdd(entityType: string, entityId: string, screenId: string): void {
    this.track('favorite_add', { entity_type: entityType, entity_id: entityId }, screenId);
  },
  
  trackFavoriteRemove(entityType: string, entityId: string, screenId: string): void {
    this.track('favorite_remove', { entity_type: entityType, entity_id: entityId }, screenId);
  },
  
  // Profile events
  trackProfileView(screenId: string): void {
    this.track('profile_view', {}, screenId);
  },
  
  trackProfileEdit(screenId: string, fields: string[]): void {
    this.track('profile_edit', { edited_fields: fields }, screenId);
  },
  
  // Quiz events
  trackQuizStart(screenId: string): void {
    this.track('quiz_start', {}, screenId);
  },
  
  trackQuizStep(screenId: string, stepIndex: number, answers?: Record<string, unknown>): void {
    this.track('quiz_step', { step_index: stepIndex, has_answers: !!answers }, screenId);
  },
  
  trackQuizComplete(screenId: string, totalSteps: number): void {
    this.track('quiz_complete', { total_steps: totalSteps }, screenId);
  },
  
  // Legacy emit method for backward compatibility
  emit(event: { event_type: string; screen_id: string; timestamp: string; payload?: Record<string, unknown> }): void {
    this.track(event.event_type, event.payload, event.screen_id);
  },
  
  // Legacy methods for backward compatibility
  emitOnboardingStart(fromPage: string = 'SYS-001'): void {
    this.trackOnboardingStart(fromPage);
  },
  
  emitOnboardingStepView(screenId: string, stepIndex: number): void {
    this.trackOnboardingStepView(screenId, stepIndex);
  },
  
  emitOnboardingSkipped(screenId: string, lastViewedStep: number): void {
    this.trackOnboardingSkipped(screenId, lastViewedStep);
  },
  
  emitOnboardingStepJump(screenId: string, fromStep: number, toStep: number): void {
    this.track('onboarding_step_jump', { from_step: fromStep, to_step: toStep }, screenId);
  },
  
  emitOnboardingCompleted(screenId: string, finalStep: number): void {
    this.trackOnboardingCompleted(screenId, finalStep);
  },
  
  emitOnboardingRoleSelect(screenId: string, role: 'adopter' | 'curator'): void {
    this.track('onboarding_role_select', { role }, screenId);
  },
  
  emitOnboardingComplete(screenId: string, role: 'adopter' | 'curator'): void {
    this.track('onboarding_complete', { role }, screenId);
  },
  
  emitCatalogViewStub(screenId: string): void {
    this.track('catalog_view_stub', { stub: true }, screenId);
  },
  
  emitTabMainView(screenId: string): void {
    this.track('tab_main_view', {}, screenId);
  },
};

// Initialize on module load
if (typeof window !== 'undefined') {
  analyticsService.init();
}

export default analyticsService;
